/**
 * F-131 — the roadmap screen's data: the server's stored result, joined to the catalogue.
 *
 * The score is computed once on the server and read as a stored result (`get_roadmap_result`);
 * this hook joins it to the roadmap points (content) and the Xero feature flag, derives each
 * point's steps, and returns the view the screens render. It computes no score. `notCalculated`
 * is true once the read has answered that nothing has been computed for the org yet.
 *
 * It used to also read the latest BSS calculation, for a `sessionId` and a `calculatedAt` that
 * no screen ever displayed — the page's "State as of" line comes from the stored result's own
 * `computedAt`. That read sat in `isLoading` and carried React Query's default three retries
 * with backoff, so a slow or failing `bss-calculator/latest` held the whole roadmap behind
 * "Building your roadmap…" for the full ladder while the score and every pillar were already in
 * hand. Its error was excluded from `error`, so the wait was silent. Removed rather than
 * patched: the fix is not to drop it from `isLoading`, it is not to make the request.
 */
import { useMemo } from 'react';
import type { CatalogueAsset, PillarCode } from '@/lib/prime-score';
import { PILLARS } from '@/lib/prime-score';
import {
  buildRoadmapView,
  deriveResultsState,
  type ResultsTrackState,
  type RoadmapView,
} from '@/lib/roadmap/result-view';
import { deriveSteps } from '@/lib/roadmap/derived-steps';
import type { DerivedStep } from '@/lib/roadmap/types';
import type { RoadmapPointPayload } from '@/api/roadmap';
import { isXeroEnabled } from '@/lib/feature-flags';
import { useRoadmapPoints, useRoadmapResult } from './use-roadmap';

export { deriveResultsState };
export type { ResultsTrackState };

const PILLAR_SET: ReadonlySet<string> = new Set(PILLARS);

/** The roadmap points as the view's catalogue: id, pillar, order, name, prerequisite. */
export function catalogueFromPoints(points: RoadmapPointPayload[]): CatalogueAsset[] {
  return points
    .filter((p) => p.is_roadmap_point && PILLAR_SET.has(p.pillar ?? ''))
    .map((p) => ({
      id: p.id,
      pillar: p.pillar as PillarCode,
      orderIndex: p.roadmap_order ?? 0,
      name: p.name,
      prerequisiteOrder: 0,
      prerequisiteId: p.prerequisite?.id ?? null,
      description: p.description ?? '',
      roles: [],
      // The rubric rows the points function already serves (`include=rubric`): the level names
      // and lines every roadmap surface labels a point with (QA rulings R2/R3, 14 Sep 2026).
      rubric: p.rubric ?? [],
    }));
}

export interface RoadmapScoreResult extends RoadmapView {
  organizationId: string;
  points: RoadmapPointPayload[];
  catalogue: CatalogueAsset[];
  stepsByAsset: Map<string, DerivedStep[]>;
}

export function useRoadmapScore(orgId: string | null, opts: { enabled: boolean }) {
  const points = useRoadmapPoints(opts.enabled);
  const result = useRoadmapResult(orgId, opts.enabled);
  const flagOn = isXeroEnabled();

  const data = useMemo<RoadmapScoreResult | null>(() => {
    if (!orgId || !points.data || !result.data) return null;
    const catalogue = catalogueFromPoints(points.data);
    const view = buildRoadmapView(result.data, catalogue, { flagOn });

    const stepsByAsset = new Map<string, DerivedStep[]>();
    const kpiContext = { rowsByCode: view.kpisByCode, figuresWithheld: view.results === 'no_access' };
    for (const point of points.data) {
      stepsByAsset.set(point.id, deriveSteps(point, result.data.points[point.id], kpiContext));
    }

    return {
      ...view,
      organizationId: orgId,
      points: points.data,
      catalogue,
      stepsByAsset,
    };
  }, [orgId, points.data, result.data, flagOn]);

  const isLoading = points.isLoading || result.isLoading;
  const error = points.error ?? result.error ?? null;

  return {
    data,
    isLoading,
    isError: !!error,
    error,
    /** The read answered: nothing has been computed for this org yet. */
    notCalculated: result.isFetched && !result.isError && result.data === null,
    resultQuery: result,
  };
}
